import { Alert } from '../../../../../shared/components/cells/Alert/Alert';
import { loading, loadingEnd } from '../../../../../shared/client/global';
import { filesUpload, imageCheck } from '../../../../../shared/client/file';
import { preventEscapeErrorJson } from '../../../../../shared/util/preventEscapeErrorJson';
import axios from 'axios';
import { find, map, pipe, toArray, toAsync } from '@fxts/core';
import {
  FanLetterFormSubmitEvent,
  FanLetterFormFileChangeEvent,
  FanLetterFormImageDeleteEvent,
} from '../events/FanLetterEvents';
import { FanLetterFormView } from '../views/FanLetterFormView';
import { FanLetterFormUploadingImagesView } from '../views/FanLetterFormUploadingImagesView';
import querystring from 'querystring';
import { ClientError } from '@services/MarppleShop/shared/client/error.ts';

interface FanLetterFormControllerImpl {
  onFileChange: (e: FanLetterFormFileChangeEvent) => void;
  onSubmitted: (e: FanLetterFormSubmitEvent) => void;
  onImageDelete: (e: FanLetterFormImageDeleteEvent) => void;
}

export class FanLetterFormController implements FanLetterFormControllerImpl {
  private uploadingImagesView: FanLetterFormUploadingImagesView;

  private __ing_events = false;

  constructor(public readonly view: FanLetterFormView) {
    this.uploadingImagesView = this.view.getUploadingFanLetterImagesView();
    this.view.addEventListener(FanLetterFormSubmitEvent, this.checkEndEvent(this.onSubmitted.bind(this)));
    this.view.addEventListener(
      FanLetterFormFileChangeEvent,
      this.checkEndEvent(this.onFileChange.bind(this)),
    );
    this.view.addEventListener(
      FanLetterFormImageDeleteEvent,
      this.checkEndEvent(this.onImageDelete.bind(this)),
    );
  }

  private toggleTextAreaHeight() {
    this.view.getFanLetterTextAreaView().toggleWithImages(this.uploadingImagesView.getFiles().length > 0);
    return this;
  }

  private checkEndEvent<T>(cb: (e: T) => Promise<unknown>) {
    return async (e: T) => {
      if (this.__ing_events) {
        return;
      }

      this.__ing_events = true;
      await cb(e);
      this.__ing_events = false;
    };
  }

  public async onFileChange(e: FanLetterFormFileChangeEvent) {
    const { is_over_max_count } = e.detail;

    if (is_over_max_count) {
      return Alert.open({
        message: ET('mps2::cheerup::txt3'),
      });
    }

    try {
      loading();
      const input_file$ = this.view.getInputFile$();
      if (input_file$?.files) {
        if (
          await pipe(
            [...input_file$.files],
            toAsync,
            find(async (file) => {
              await imageCheck(file, {
                max_file_volume: 10,
                accepts: ['jpg', 'jpeg', 'png', 'webp', 'gif'],
                max_sizes: { width: 5000, height: 5000 },
              });
              return file.type.indexOf('image') === -1;
            }),
          )
        ) {
          return await Alert.open({ message: T('community::파일 업로드 실패했습니다.') });
        }

        const files = await filesUpload(input_file$);
        this.uploadingImagesView.pushFiles(files).redraw();
        this.toggleTextAreaHeight();
      }
    } catch (err) {
      loadingEnd();
      if (err instanceof XMLHttpRequest) {
        if (err.responseText?.length && err.responseText.indexOf('File too large') != -1) {
          await Alert.open({ message: T('File size is too large') });
        } else {
          await Alert.open({ message: T('community::파일 업로드 실패했습니다.') });
        }
      } else if (err instanceof ClientError) {
        return Alert.open(preventEscapeErrorJson(err.json));
      } else {
        await Alert.open(
          preventEscapeErrorJson({
            title: ET('mps2::common::error_title'),
            message: ET('mps2::common::error_message'),
          }),
        );
      }
    } finally {
      loadingEnd();
      this.view.initInputFile$();
    }
  }

  public async onSubmitted(e: FanLetterFormSubmitEvent) {
    const { type } = e.detail;
    loading();
    try {
      const { data } = await axios.post(`/${T.lang}/@api/stores/${this.view.data.creator.id}/community`, {
        type,
        body: this.view.getFanLetterTextAreaView().getTextValue().trim(),
        photos: pipe(
          this.view.getUploadingFanLetterImagesView().getFiles(),
          map((file) => {
            return {
              id: file.id,
              url: file.url,
              width: file.width,
              height: file.height,
            };
          }),
          toArray,
        ),
      });
      if (!data.result) {
        loadingEnd();
        return Alert.open({ message: data.msg || T('req_msg::failed please retry') });
      }
    } catch (err) {
      /* API에 에러주는곳이 없음 */
      loadingEnd();
      if (err instanceof Error) {
        return Alert.open({ message: err.message });
      } else {
        return Alert.open({ message: T('req_msg::failed please retry') });
      }
    }
    const query = querystring.parse(location.search.slice(1));
    if (query.page) {
      delete query.page;
      const query_str = querystring.stringify(query);
      window.location.replace(`${location.pathname}${query_str ? `?${query_str}` : ''}`);
    } else {
      window.location.reload();
    }
  }

  public async onImageDelete(e: FanLetterFormImageDeleteEvent) {
    const { file_id } = e.detail;
    this.uploadingImagesView.deleteFiles(file_id);
    this.uploadingImagesView.redraw();
    this.toggleTextAreaHeight();
  }
}
